import { RotateCcw, TriangleAlert } from "lucide-react";
import { useNavigate } from "react-router";

import { HeaderText } from "@src/shared/components/general/HeaderText";
import { Text } from "@src/shared/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Button } from "@src/shared/components/ui/button";
import { navigation } from "@src/shared/navigation";

export interface ApplicationErrorPageProps {
  /**
   * Optional headline shown above the description. Defaults to a generic
   * "Something went wrong" copy when omitted.
   */
  title?: string;
  /**
   * Short, user-facing explanation of what happened. Defaults to a generic
   * apology when omitted. Sensitive details (stack traces, raw error
   * messages) should not be passed here.
   */
  description?: string;
}

/**
 * Generic full-page fallback rendered by the root `ErrorBoundary` for any
 * error that isn't a 404. Mirrors the visual language of `PageNotFoundError`
 * so the application never falls back to unstyled error markup.
 */
export const ApplicationErrorPage = ({
  title = "Something went wrong",
  description = "An unexpected error occurred. Please try again, or head back to the dashboard.",
}: ApplicationErrorPageProps) => {
  const navigate = useNavigate();

  return (
    <VerticalFlexbox
      alignItems="center"
      justifyContent="center"
      gap="md"
      className="min-h-[60vh] w-full px-6 py-12 text-center"
    >
      <VerticalFlexbox
        alignItems="center"
        justifyContent="center"
        className="size-16 rounded-full bg-destructive/10 text-destructive"
      >
        <TriangleAlert className="size-8" />
      </VerticalFlexbox>
      <HeaderText tag="h1" className="text-3xl">
        {title}
      </HeaderText>
      <Text sizing="sm" appearance="subdued" className="max-w-md">
        {description}
      </Text>
      <HorizontalFlexbox
        alignItems="center"
        justifyContent="center"
        gap="sm"
        className="mt-2"
      >
        <Button
          variant="outline"
          onClick={() => {
            window.location.reload();
          }}
        >
          <RotateCcw className="mr-1 size-4" />
          Try again
        </Button>
        <Button
          className="bg-accent text-accent-foreground hover:bg-accent/90"
          onClick={() => navigate(navigation.dashboard())}
        >
          Back to Dashboard
        </Button>
      </HorizontalFlexbox>
    </VerticalFlexbox>
  );
};
